/**
 * ExpertSetupDesk — Architecture 3.1 专家配置工坊
 *
 * 律师的"零代码 Agent 定制工作台"。
 * 左栏：配置向导（Profile → SOP → 工具授权 → HITL 安全开关）
 * 右栏：实时沙盒测试聊天区
 *
 * 遵循 Institutional Minimalist 设计语言 + 律师行业术语降级。
 */

import React, { useState, useEffect } from 'react';
import {
    User, FileText, Wrench, ShieldCheck, Send, Loader2,
    CheckCircle, AlertTriangle, Save, Sparkles,
} from 'lucide-react';
import { WorkflowApiService, SkillItem, CreateAgentRequest } from '../../services/workflowApi';

interface ExpertSetupDeskProps {
    onSaved?: () => void;
    currentLanguage: string;
}

const ExpertSetupDesk: React.FC<ExpertSetupDeskProps> = ({ onSaved, currentLanguage }) => {
    // ── 表单状态 ──
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [personaPrompt, setPersonaPrompt] = useState('');
    const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
    const [securityLevel, setSecurityLevel] = useState<'standard' | 'hitl_required'>('standard');
    const [visibility, setVisibility] = useState<'private' | 'public'>('private');

    // ── 数据加载 ──
    const [availableSkills, setAvailableSkills] = useState<SkillItem[]>([]);
    const [isLoadingSkills, setIsLoadingSkills] = useState(true);

    // ── 保存状态 ──
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);

    // ── 沙盒状态 ──
    const [sandboxMessages, setSandboxMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
    const [sandboxInput, setSandboxInput] = useState('');
    const [isSandboxLoading, setIsSandboxLoading] = useState(false);

    // ── SOP 模板库 ──
    const sopTemplates = [
        {
            label: '合同审查 SOP',
            value: '你是一名资深的合同审查律师。当用户提交合同文本时，你应当：\n1. 逐条审查所有条款，标注潜在的法律风险\n2. 重点关注违约责任、管辖条款、终止条件\n3. 按照"高风险/中风险/低风险"三档分级标注\n4. 在最后给出一份合规改善建议清单',
        },
        {
            label: '尽职调查 SOP',
            value: '你是一名并购尽调领域的法律助手。你的任务是：\n1. 全面检索目标公司的主体资质和变更历史\n2. 梳理重大诉讼与仲裁记录\n3. 审查知识产权权属和许可状态\n4. 输出结构化的《尽职调查问题清单》',
        },
        {
            label: '法律检索 SOP',
            value: '你是一名精通多法域法律检索的研究助理。当用户描述一个法律问题时：\n1. 首先明确适用的法律体系与管辖地\n2. 检索最相关的法条、司法解释和指导案例\n3. 按照"Findings → Summary → References"的结构输出\n4. 所有引用必须附带正式的法律文献编号',
        },
    ];

    // ── 加载可用工具 ──
    useEffect(() => {
        setIsLoadingSkills(true);
        WorkflowApiService.getSkills()
            .then(res => setAvailableSkills(res.items || []))
            .catch(err => {
                console.error('[ExpertSetupDesk] Failed to load skills:', err);
                setAvailableSkills([]);
            })
            .finally(() => setIsLoadingSkills(false));
    }, []);

    // ── 工具勾选 ──
    const toggleSkill = (skillId: string) => {
        setSelectedSkills(prev => {
            const next = new Set(prev);
            if (next.has(skillId)) {
                next.delete(skillId);
            } else {
                next.add(skillId);
            }
            return next;
        });
    };

    // ── 保存专家 ──
    const handleSave = async () => {
        if (!name.trim() || !personaPrompt.trim()) {
            setSaveResult({ success: false, message: '请填写专家名称和工作指导原则 (SOP)' });
            return;
        }

        setIsSaving(true);
        setSaveResult(null);

        try {
            const data: CreateAgentRequest = {
                name: name.trim(),
                description: description.trim() || undefined,
                persona_prompt: personaPrompt.trim(),
                // 将选中的 Skill UUID 包装为多态装备对象
                equipped_skills: Array.from(selectedSkills).map(id => ({
                    type: 'tool' as const,
                    id,
                })),
                security_level: securityLevel,
                visibility,
            };

            await WorkflowApiService.createAgent(data);
            setSaveResult({ success: true, message: `专家「${name}」已成功创建！` });
            onSaved?.();
        } catch (err: any) {
            setSaveResult({ success: false, message: `创建失败：${err.message}` });
        } finally {
            setIsSaving(false);
        }
    };

    // ── 沙盒发送 ──
    const handleSandboxSend = async () => {
        if (!sandboxInput.trim() || !personaPrompt.trim()) return;

        const userMsg = sandboxInput.trim();
        setSandboxInput('');
        setSandboxMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setIsSandboxLoading(true);

        try {
            // 使用临时会话 ID（沙盒专用，不污染正式对话列表）
            const sandboxSessionId = `sandbox_${Date.now()}`;

            // 构建带 Persona 上下文前缀的消息，让后端把 SOP 作为系统指令
            const sandboxMessage = `[SANDBOX MODE — 以下是用户为此专家配置的 SOP 指令，请严格遵循]\n\n${personaPrompt}\n\n[用户问题]\n${userMsg}`;

            const { API_BASE_URL } = await import('../../services/api');

            const token = localStorage.getItem('auth_token');
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    message: sandboxMessage,
                    conversationId: sandboxSessionId,
                }),
            });

            if (!response.ok || !response.body) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let assistantContent = '';
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const event = JSON.parse(line.slice(6).trim());
                        if (event.type === 'content' && event.text) {
                            assistantContent += event.text;
                            // 实时更新最后一条 assistant 消息
                            setSandboxMessages(prev => {
                                const last = prev[prev.length - 1];
                                if (last?.role === 'assistant') {
                                    return [...prev.slice(0, -1), { role: 'assistant', content: assistantContent }];
                                }
                                return [...prev, { role: 'assistant', content: assistantContent }];
                            });
                        } else if (event.type === 'done' || event.type === 'error') {
                            break;
                        }
                    } catch { /* skip malformed SSE lines */ }
                }
            }

            reader.releaseLock();

            // 如果流结束但没有收到任何内容
            if (!assistantContent) {
                setSandboxMessages(prev => [...prev, {
                    role: 'assistant',
                    content: '[沙盒] 未收到 AI 回复，请检查后端连接。',
                }]);
            }
        } catch (err: any) {
            setSandboxMessages(prev => [...prev, {
                role: 'assistant',
                content: `[沙盒错误] ${err.message || '连接失败'}。请确认后端已启动。`,
            }]);
        } finally {
            setIsSandboxLoading(false);
        }
    };

    // ── 按分类整理工具 ──
    const skillsByCategory = availableSkills.reduce<Record<string, SkillItem[]>>((acc, skill) => {
        const cat = skill.category || '其他';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(skill);
        return acc;
    }, {});

    return (
        <div className="flex h-full gap-6">
            {/* ═══ 左栏：配置向导 ═══ */}
            <div className="flex-1 overflow-y-auto pr-4 space-y-6">

                {/* 模块 1：基本档案 */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4">
                        <User size={16} className="text-purple-500" />
                        基本档案
                    </h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">专家名称 *</label>
                            <input
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="例如：公司并购法专家"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 bg-gray-50 focus:bg-white transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">执业简介</label>
                            <input
                                type="text"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="简要描述这位专家的执业领域和特长"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 bg-gray-50 focus:bg-white transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* 模块 2：工作指导原则 (SOP) */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                        <FileText size={16} className="text-blue-500" />
                        工作指导原则 (SOP) *
                    </h3>
                    <p className="text-xs text-gray-400 mb-3">
                        用大白话告诉这位专家"你的工作职责是什么"、"遇到什么情况该怎么处理"。
                    </p>
                    {/* 快速模板 */}
                    <div className="flex gap-2 mb-3 flex-wrap">
                        {sopTemplates.map(tpl => (
                            <button
                                key={tpl.label}
                                onClick={() => setPersonaPrompt(tpl.value)}
                                className="text-xs px-3 py-1.5 rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                            >
                                {tpl.label}
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={personaPrompt}
                        onChange={e => setPersonaPrompt(e.target.value)}
                        rows={8}
                        placeholder="例如：你是一名资深的公司法律师。当用户提交合同时，你应当逐条审查所有条款，标注潜在的法律风险..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-200 bg-gray-50 focus:bg-white transition-all resize-vertical"
                    />
                </div>

                {/* 模块 3：授权工具箱 */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                        <Wrench size={16} className="text-amber-500" />
                        授权工具箱
                    </h3>
                    <p className="text-xs text-gray-400 mb-4">
                        勾选此专家被授权使用的外部工具。未勾选的工具，该专家将无权调用。
                    </p>

                    {isLoadingSkills ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 size={20} className="text-gray-300 animate-spin" />
                        </div>
                    ) : availableSkills.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">
                            暂无可用工具。系统工具将在首次运行迁移脚本后自动注册。
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {Object.entries(skillsByCategory).map(([category, skills]) => (
                                <div key={category}>
                                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                        {category}
                                    </h4>
                                    <div className="space-y-1">
                                        {skills.map(skill => (
                                            <label
                                                key={skill.id}
                                                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSkills.has(skill.id)}
                                                    onChange={() => toggleSkill(skill.id)}
                                                    className="w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-purple-200"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-gray-700 font-medium">{skill.display_name}</div>
                                                    {skill.description && (
                                                        <div className="text-xs text-gray-400 truncate">{skill.description}</div>
                                                    )}
                                                </div>
                                                {skill.is_builtin && (
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-500 border border-blue-100">
                                                        系统内置
                                                    </span>
                                                )}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 模块 4：安全与合规 */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-3">
                        <ShieldCheck size={16} className="text-amber-600" />
                        审批与安全阀
                    </h3>
                    <p className="text-xs text-amber-700/70 mb-4">
                        控制此专家在生成最终输出前，是否需要您的亲自批准。开启此功能可以最大程度防止 AI 擅自做主。
                    </p>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <div
                            onClick={() => setSecurityLevel(prev => prev === 'standard' ? 'hitl_required' : 'standard')}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                                securityLevel === 'hitl_required' ? 'bg-amber-500' : 'bg-gray-300'
                            }`}
                        >
                            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                securityLevel === 'hitl_required' ? 'translate-x-6' : 'translate-x-0.5'
                            }`} />
                        </div>
                        <span className="text-sm text-gray-700 font-medium">
                            {securityLevel === 'hitl_required'
                                ? '✅ 已开启：最终输出前强制要求本人批准'
                                : '⚠️ 未开启：专家将自动提交最终结果'
                            }
                        </span>
                    </label>
                </div>

                {/* 保存按钮 */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSave}
                        disabled={isSaving || !name.trim() || !personaPrompt.trim()}
                        className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                        {isSaving ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Save size={16} />
                        )}
                        保存专家
                    </button>

                    {saveResult && (
                        <div className={`flex items-center gap-2 text-sm ${
                            saveResult.success ? 'text-green-600' : 'text-red-500'
                        }`}>
                            {saveResult.success ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                            {saveResult.message}
                        </div>
                    )}
                </div>
            </div>

            {/* ═══ 右栏：沙盒测试区 ═══ */}
            <div className="w-[380px] flex-shrink-0 flex flex-col bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                {/* 沙盒头部 */}
                <div className="px-4 py-3 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-purple-400" />
                        <h3 className="text-sm font-semibold text-gray-700">沙盒测试区</h3>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                        在此处测试您的专家配置。测试对话不会被记录到正式案卷中。
                    </p>
                </div>

                {/* 沙盒消息区 */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {sandboxMessages.length === 0 && (
                        <div className="text-center pt-16 text-gray-300">
                            <Sparkles size={32} className="mx-auto mb-3 opacity-40" />
                            <p className="text-sm">在左侧配好 SOP 后</p>
                            <p className="text-sm">在这里发一条消息测试</p>
                        </div>
                    )}
                    {sandboxMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                                msg.role === 'user'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-white border border-gray-200 text-gray-700'
                            }`}>
                                <pre className="whitespace-pre-wrap font-sans">{msg.content}</pre>
                            </div>
                        </div>
                    ))}
                    {isSandboxLoading && (
                        <div className="flex justify-start">
                            <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl">
                                <Loader2 size={16} className="text-purple-400 animate-spin" />
                            </div>
                        </div>
                    )}
                </div>

                {/* 沙盒输入区 */}
                <div className="p-3 border-t border-gray-200 bg-white">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={sandboxInput}
                            onChange={e => setSandboxInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSandboxSend()}
                            placeholder={personaPrompt ? '输入测试问题...' : '请先填写 SOP 指令'}
                            disabled={!personaPrompt.trim()}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all disabled:opacity-40"
                        />
                        <button
                            onClick={handleSandboxSend}
                            disabled={!sandboxInput.trim() || !personaPrompt.trim() || isSandboxLoading}
                            className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors disabled:opacity-30"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpertSetupDesk;
